import { Link } from "react-router-dom";
import heroBanner from "@/assets/hero-banner.webp";
import heroBannerSm from "@/assets/hero-banner-sm.webp";
import { motion } from "framer-motion";
import { Star, Heart, Sparkles, MessageCircle, Sun, Zap } from "lucide-react";
import PricingSection from "@/components/PricingSection";
import StarField from "@/components/StarField";
import MoonVisual from "@/components/MoonVisual";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CommunityStats from "@/components/CommunityStats";
import LiveUsersIndicator from "@/components/LiveUsersIndicator";
import ActivityFeed from "@/components/ActivityFeed";
import { zodiacSigns } from "@/data/zodiac";
import { useState } from "react";
import CosmicEmailPopup from "@/components/CosmicEmailPopup";
import SEO from "@/components/SEO";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const Index = () => {
  const [sign1, setSign1] = useState("Aries");
  const [sign2, setSign2] = useState("Libra");

  return (
    <>
      <SEO title="Nebula — AI Astrology, Daily Horoscopes & Cosmic Insights" description="Discover your cosmic destiny with AI-powered daily horoscopes, tarot readings, birth charts, compatibility insights and personal cosmic guidance." path="/" />
      <div className="min-h-screen bg-background relative overflow-hidden">
      <CosmicEmailPopup />
      <StarField />
      <Navbar />

      <main>
      {/* Hero */}
      <section className="relative min-h-screen flex flex-col items-center justify-center pt-16">
        <div className="container mx-auto px-4 flex flex-col items-center text-center relative z-10">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.2 }} className="mb-8">
            <MoonVisual size={180} />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}
            className="font-display text-4xl md:text-6xl lg:text-7xl font-bold cosmic-text-glow max-w-4xl leading-tight"
          >
            Discover What The Universe Has Planned For You
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}
            className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl"
          >
            Nebula is your personal AI astrologer delivering daily horoscopes, compatibility insights and cosmic guidance.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/signup" className="btn-cosmic text-base">Get My Horoscope</Link>
            <Link to="/signup" className="btn-gold text-base">Ask Nebula</Link>
          </motion.div>

          {/* Hero Banner */}
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.9, duration: 1, ease: "easeOut" }}
            className="mt-16 w-full max-w-5xl"
          >
            <div className="relative rounded-2xl overflow-hidden border border-primary/20 cosmic-glow shadow-2xl shadow-primary/10">
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent z-10 pointer-events-none" />
              <img
                src={heroBanner}
                srcSet={`${heroBannerSm} 700w, ${heroBanner} 1179w`}
                sizes="(max-width: 768px) 100vw, (max-width: 1280px) 80vw, 1024px"
                alt="Nebula — Unlock Your Cosmic Path with AI Astrology, Tarot, Predictions and Soul Connections"
                className="w-full h-auto object-cover"
                width={1024}
                height={688}
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </section>

      <LiveUsersIndicator />
      <CommunityStats onEvent={(events) => (window as any).__nebulaActivityPush?.(events)} />

      {/* Daily Horoscope */}
      <section className="relative z-10 py-24">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-5xl font-bold cosmic-text-glow">Daily Horoscope</h2>
            <p className="mt-4 text-muted-foreground text-lg">Your cosmic forecast for today</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {zodiacSigns.map((sign, i) => (
              <motion.div
                key={sign.name}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="cosmic-card rounded-xl p-5 hover:border-primary/30 transition-all duration-300 group cursor-pointer"
              >
                <div className="text-4xl mb-3">{sign.symbol}</div>
                <h3 className="font-display text-lg font-semibold">{sign.name}</h3>
                <p className="text-xs text-muted-foreground mt-1">{sign.dates}</p>
                <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{sign.preview}</p>
                <Link to="/signup" className="mt-3 inline-block text-xs text-primary hover:text-cosmic-gold transition-colors">
                  Read More →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compatibility */}
      <section className="relative z-10 py-24">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-5xl font-bold cosmic-text-glow">Zodiac Compatibility</h2>
            <p className="mt-4 text-muted-foreground text-lg">Discover your cosmic connection</p>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-lg mx-auto cosmic-card rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <select aria-label="Your zodiac sign" value={sign1} onChange={(e) => setSign1(e.target.value)} className="bg-secondary text-foreground rounded-lg px-4 py-2 text-sm border border-border">
                {zodiacSigns.map((s) => <option key={s.name} value={s.name}>{s.symbol} {s.name}</option>)}
              </select>
              <Heart className="text-cosmic-gold w-6 h-6" />
              <select aria-label="Partner zodiac sign" value={sign2} onChange={(e) => setSign2(e.target.value)} className="bg-secondary text-foreground rounded-lg px-4 py-2 text-sm border border-border">
                {zodiacSigns.map((s) => <option key={s.name} value={s.name}>{s.symbol} {s.name}</option>)}
              </select>
            </div>
            <div className="cosmic-card rounded-xl p-6 mb-6">
              <p className="font-display text-xl font-semibold">{sign1} + {sign2}</p>
              <p className="text-cosmic-gold text-2xl font-bold mt-2">Love Compatibility: 82%</p>
            </div>
            <Link to="/signup" className="btn-cosmic text-sm">Check Compatibility</Link>
          </motion.div>
        </div>
      </section>

      {/* Tarot */}
      <section className="relative z-10 py-24">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-5xl font-bold cosmic-text-glow">Tarot Insights</h2>
            <p className="mt-4 text-muted-foreground text-lg">Let the cards reveal your destiny</p>
          </motion.div>
          <div className="flex justify-center gap-6 mb-10 flex-wrap">
            {["The Fool", "The Magician", "The High Priestess"].map((card, i) => (
              <motion.div
                key={card}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="w-40 h-60 cosmic-card rounded-xl flex flex-col items-center justify-center cosmic-glow hover:scale-105 transition-transform duration-300 cursor-pointer"
              >
                <Star className="w-10 h-10 text-cosmic-gold mb-3" />
                <p className="font-display text-sm font-semibold text-center px-2">{card}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center space-y-4">
            <div className="flex flex-wrap justify-center gap-3 text-sm text-muted-foreground">
              <span className="cosmic-card rounded-full px-4 py-1.5">✦ Single Card Reading</span>
              <span className="cosmic-card rounded-full px-4 py-1.5">✦ Three Card Spread</span>
              <span className="cosmic-card rounded-full px-4 py-1.5">✦ Full Tarot Guidance</span>
            </div>
            <Link to="/signup" className="btn-cosmic text-sm inline-block">Generate Tarot Reading</Link>
          </div>
        </div>
      </section>

      {/* AI Astrologer */}
      <section className="relative z-10 py-24">
        <div className="container mx-auto px-4">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-5xl font-bold cosmic-text-glow">AI Astrologer</h2>
            <p className="mt-4 text-muted-foreground text-lg">Ask Nebula about love, career, relationships and life decisions</p>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-md mx-auto cosmic-card rounded-2xl p-6 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <MessageCircle className="w-4 h-4 text-primary" />
              </div>
              <div className="cosmic-card rounded-xl rounded-tl-sm px-4 py-3 text-sm">
                Will my relationship work out this year?
              </div>
            </div>
            <div className="flex items-start gap-3 flex-row-reverse">
              <div className="w-8 h-8 rounded-full bg-cosmic-gold/20 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-cosmic-gold" />
              </div>
              <div className="bg-primary/10 border border-primary/20 rounded-xl rounded-tr-sm px-4 py-3 text-sm">
                The stars suggest a period of deep growth in your relationship. Venus enters your 7th house in March, bringing harmony and renewal...
              </div>
            </div>
            <div className="text-center pt-3">
              <Link to="/signup" className="btn-cosmic text-sm">Ask The AI Astrologer</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Birth Chart */}
      <section className="relative z-10 py-24">
        <div className="container mx-auto px-4 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="font-display text-3xl md:text-5xl font-bold cosmic-text-glow">Birth Chart</h2>
            <p className="mt-4 text-muted-foreground text-lg max-w-xl mx-auto">Generate your personal astrology blueprint using your birth date, time and location.</p>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-10 max-w-sm mx-auto cosmic-card rounded-2xl p-8 space-y-4">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Sun className="w-5 h-5 text-cosmic-gold" /> Birth Date
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Star className="w-5 h-5 text-cosmic-gold" /> Birth Time (optional)
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Zap className="w-5 h-5 text-cosmic-gold" /> Birth Location
            </div>
            <Link to="/signup" className="btn-cosmic text-sm inline-block mt-4">Generate My Birth Chart</Link>
          </motion.div>
        </div>
      </section>

      <PricingSection />

      <ActivityFeed />
      </main>
      <Footer />
    </div>
    </>

  );
};

export default Index;
